import { motion } from "framer-motion";
import Grid from "../Grid/grid";
import {
  ChevronLeft,
  ChevronRight,
  Target,
  Lightbulb,
  CheckCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
};

function HeadSection({ work }: { work: any }) {
  const navigate = useNavigate();

  return (
    <>
      <motion.div
        key={work?.id}
        className="relative flex flex-col items-center justify-center min-h-screen px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 py-32"
        style={{
          backgroundColor: `#${work?.bg_color}`,
        }}
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <motion.div
          className="w-full max-w-6xl mx-auto"
          variants={itemVariants}
        >
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20 mb-20 md:mb-28">
            <div
              className="w-full lg:w-1/2 flex flex-col items-start justify-center space-y-8 md:space-y-10"
              style={{
                color: `#${work?.text_color}`,
              }}
            >
              <motion.div className="space-y-6" variants={itemVariants}>
                <motion.h1
                  className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[0.9]"
                  style={{ color: `#${work?.text_color}` }}
                >
                  {work?.title}
                </motion.h1>
                <motion.div
                  className="w-16 h-0.5 bg-current opacity-60"
                  initial={{ width: 0 }}
                  animate={{ width: 64 }}
                  transition={{ delay: 0.5, duration: 0.8 }}
                />
              </motion.div>

              <motion.div className="space-y-4" variants={itemVariants}>
                <h3 className="text-sm uppercase tracking-[0.2em] font-medium opacity-70">
                  Overview
                </h3>
                <p className="text-lg md:text-xl leading-relaxed opacity-80 font-light max-w-md">
                  {work?.description}
                </p>
              </motion.div>
            </div>

            <motion.div
              className="w-full lg:w-1/2 flex items-center justify-center"
              variants={itemVariants}
            >
              <motion.img
                src={work?.main_image_url}
                alt={work?.title}
                className="w-full max-w-lg h-auto transition-transform duration-500"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              />
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
      <div className="h-auto flex flex-col gap-8 items-start w-full md:p-16 p-8">
        {work?.problem_statement && (
          <motion.div className="space-y-4" variants={itemVariants}>
            <h3 className="text-sm uppercase tracking-[0.2em] font-medium opacity-70">
              Problem Statement
            </h3>
            <p className="text-lg md:text-xl leading-relaxed opacity-80 font-light">
              {work?.problem_statement}
            </p>
          </motion.div>
        )}

        <motion.div
          className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 mb-16 w-full"
          variants={containerVariants}
        >
          <motion.div className="space-y-8" variants={itemVariants}>
            <div className="flex items-center gap-4">
              <Target className="w-5 h-5 opacity-60" />
              <h2 className="text-2xl md:text-3xl font-light">Challenges</h2>
            </div>
            <div className="space-y-6 pl-9">
              {work?.challenges?.map((challenge: string, index: number) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{
                    delay: index * 0.1,
                    duration: 0.4,
                    ease: "easeOut",
                  }}
                  className="flex items-start gap-4 group"
                >
                  <span className="text-base md:text-lg leading-relaxed font-light opacity-80">
                    {challenge}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div className="space-y-8" variants={itemVariants}>
            <div className="flex items-center gap-4">
              <Lightbulb className="w-5 h-5 opacity-60" />
              <h2 className="text-2xl md:text-3xl font-light">Solutions</h2>
            </div>
            <div className="space-y-6 pl-9">
              {work?.solutions?.map((solution: string, index: number) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{
                    delay: index * 0.1,
                    duration: 0.4,
                    ease: "easeOut",
                  }}
                  className="flex items-start gap-4 group"
                >
                  <span className="text-base md:text-lg leading-relaxed font-light opacity-80">
                    {solution}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>

        {work?.approach && work.approach.length > 0 && (
          <motion.div className="w-full space-y-10" variants={itemVariants}>
            <div className="flex items-center gap-4">
              <CheckCircle className="w-5 h-5 opacity-60" />
              <h2 className="text-2xl md:text-3xl font-light">Our Approach</h2>
            </div>
            <div className="space-y-6 pl-9">
              {work?.approach?.map((approach: string, index: number) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{
                    delay: index * 0.1,
                    duration: 0.4,
                    ease: "easeOut",
                  }}
                  className="flex items-start gap-4 group"
                >
                  <span className="text-base md:text-lg leading-relaxed font-light opacity-80">
                    {approach}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </>
  );
}

export default HeadSection;
